import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import Toast from "./Toast";
import "./VendorProfile.css";

const baseURL = process.env.REACT_APP_API_BASE_URL;
const API_KEY = process.env.REACT_APP_BROWSER_API_KEY;

export default function VendorProfile() {
  const history = useHistory();
  const [formData, setFormData] = useState({
    field: "photography",
    studioname: "",
    name: "",
    email: "",
    phone: "",
    studio_logo: "",
    state: "",
    gst: "",
  });
  const [preview, setPreview] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ open: false, type: "info", message: "" });
  const logoInputRef = useRef(null);

  useEffect(() => {
    const vendorId = localStorage.getItem("vendorId");
    if (!vendorId) {
      history.replace("/");
      return;
    }
    fetchProfile(vendorId);
  }, []);

  const fetchProfile = async (vendorId) => {
    try {
      setIsLoading(true);
      const url = `${baseURL}/vendor?vendor_id=${encodeURIComponent(vendorId)}`;
      const response = await fetch(url, {
        method: "GET",
        headers: { "x-api-key": API_KEY },
      });
      if (!response.ok) throw new Error("Failed to fetch profile");
      const data = await response.json();
      const vendor = data.vendor || data;

      // mobile comes as ["+916361407737"] — strip +91 to get 10-digit number
      const rawMobile = Array.isArray(vendor.mobile) ? vendor.mobile[0] : (vendor.mobile || "");
      const phone = rawMobile.replace(/^\+91/, "");

      setFormData({
        field: vendor.field || "photography",
        studioname: vendor.studio_name || vendor.studioname || "",
        name: vendor.name || "",
        email: vendor.email || "",
        phone,
        studio_logo: vendor.studio_logo || "",
        state: vendor.state || "",
        gst: vendor.gst || "",
      });
      setPreview(vendor.studio_logo || "");
    } catch (err) {
      setToast({ open: true, type: "error", message: "Could not load profile. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

  const handleGstChange = (e) => {
    const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (raw.length > 15) return;
    setFormData((prev) => ({ ...prev, gst: raw }));
    if (!raw) {
      setErrors((prev) => ({ ...prev, gst: "" }));
    } else if (raw.length < 15) {
      setErrors((prev) => ({ ...prev, gst: `${15 - raw.length} more character${15 - raw.length > 1 ? "s" : ""} needed` }));
    } else if (!GST_REGEX.test(raw)) {
      setErrors((prev) => ({ ...prev, gst: "Invalid GST format (e.g. 22ABCDE1234F1Z5)" }));
    } else {
      setErrors((prev) => ({ ...prev, gst: "" }));
    }
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const res = await axios.post(
        `${baseURL}/upload-urls`,
        { files: [{ name: file.name, type: file.type || "image/jpeg" }], folder: "logos", bucket: "original", metadata: { asset_type: "studio_logo" } },
        { headers: { "Content-Type": "application/json", "x-api-key": API_KEY } }
      );
      const item = res.data?.uploadUrls?.[0];
      if (!item?.url) throw new Error("No signed URL returned");
      await axios.put(item.url, file, { headers: { "Content-Type": file.type || "image/jpeg" } });
      const publicUrl = item.url.split("?")[0];
      setFormData((prev) => ({ ...prev, studio_logo: publicUrl }));
      setPreview(publicUrl);
    } catch (err) {
      setToast({ open: true, type: "error", message: "Logo upload failed. Please try again." });
    } finally {
      setLogoUploading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.studioname.trim()) newErrors.studioname = "Studio name is required";
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Phone must be 10 digits";
    }
    if (!formData.state) newErrors.state = "State is required";
    if (formData.gst && !GST_REGEX.test(formData.gst)) {
      newErrors.gst = "Enter a valid GST number (e.g. 22ABCDE1234F1Z5)";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const vendorId = localStorage.getItem("vendorId");
      const payload = {
        vendor_id: vendorId,
        field: formData.field,
        studio_name: formData.studioname,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        studio_logo: formData.studio_logo,
        state: formData.state,
        ...(formData.gst && { gst: formData.gst }),
      };

      const body = payload;
      const url = `${baseURL}/vendors`;


      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY,
        },
        body: JSON.stringify(body),
      });

      const resData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(resData.message || resData.errorMessage || "Update failed");
      }

      try { localStorage.setItem("vendorDisplayName", formData.name); } catch (_) {}
      setToast({ open: true, type: "success", message: "Profile updated successfully!" });
    } catch (err) {
      setToast({ open: true, type: "error", message: err.message || "Failed to update profile" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="vp-loading">
        <div className="vp-spinner" />
        <span>Loading profile…</span>
      </div>
    );
  }

  return (
    <div className="vp-wrapper">
      <Toast open={toast.open} type={toast.type} message={toast.message} onClose={() => setToast({ ...toast, open: false })} />

      <div className="vp-container">
        {/* Back */}
        <button className="vp-back-btn" onClick={() => history.goBack()}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>

        {/* Page header */}
        <div className="vp-header">
          <div className="vp-avatar-wrap">
            {preview ? (
              <img src={preview} alt="Studio logo" className="vp-avatar" onError={(e) => { e.target.onerror = null; e.target.src = ""; setPreview(""); }} />
            ) : (
              <div className="vp-avatar-placeholder">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#F80353" strokeWidth="1.5">
                  <path d="M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
                  <circle cx="12" cy="13" r="3" />
                  <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </div>
            )}
          </div>
          <div className="vp-header-text">
            <h1 className="vp-title">Photographer Profile</h1>
            <p className="vp-subtitle">Manage your studio information and contact details.</p>
          </div>
        </div>

        {/* Form */}
        <form className="vp-form" onSubmit={handleSubmit}>

          <div className="vp-row">
            <div className="vp-field-group">
              <label className="vp-label">Studio Name <span style={{ color: '#F80353' }}>*</span></label>
              <input
                type="text"
                name="studioname"
                className={`vp-input${errors.studioname ? " vp-input-error" : ""}`}
                value={formData.studioname}
                onChange={handleChange}
                placeholder="e.g. Nice Studios"
              />
              {errors.studioname && <span className="vp-error">{errors.studioname}</span>}
            </div>

            <div className="vp-field-group">
              <label className="vp-label">Your Name <span style={{ color: '#F80353' }}>*</span></label>
              <input
                type="text"
                name="name"
                className={`vp-input${errors.name ? " vp-input-error" : ""}`}
                value={formData.name}
                onChange={handleChange}
                placeholder="Full name"
              />
              {errors.name && <span className="vp-error">{errors.name}</span>}
            </div>
          </div>

          <div className="vp-row">
            <div className="vp-field-group">
              <label className="vp-label">Email Address <span style={{ color: '#F80353' }}>*</span></label>
              <input
                type="email"
                name="email"
                className={`vp-input${errors.email ? " vp-input-error" : ""}`}
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
              />
              {errors.email && <span className="vp-error">{errors.email}</span>}
            </div>

            <div className="vp-field-group">
              <label className="vp-label">Phone Number <span style={{ color: '#F80353' }}>*</span></label>
              <input
                type="text"
                name="phone"
                className={`vp-input${errors.phone ? " vp-input-error" : ""}`}
                value={formData.phone}
                onChange={(e) => {
                  if (/^\d{0,10}$/.test(e.target.value)) handleChange(e);
                }}
                placeholder="10-digit mobile number"
                maxLength={10}
              />
              {errors.phone && <span className="vp-error">{errors.phone}</span>}
            </div>
          </div>

          <div className="vp-row">
            <div className="vp-field-group">
              <label className="vp-label">State <span style={{ color: '#F80353' }}>*</span></label>
              <select
                name="state"
                className={`vp-input${errors.state ? " vp-input-error" : ""}`}
                value={formData.state}
                onChange={handleChange}
              >
                <option value="">Select your state</option>
                <option>Andhra Pradesh</option>
                <option>Arunachal Pradesh</option>
                <option>Assam</option>
                <option>Bihar</option>
                <option>Chhattisgarh</option>
                <option>Goa</option>
                <option>Gujarat</option>
                <option>Haryana</option>
                <option>Himachal Pradesh</option>
                <option>Jharkhand</option>
                <option>Karnataka</option>
                <option>Kerala</option>
                <option>Madhya Pradesh</option>
                <option>Maharashtra</option>
                <option>Manipur</option>
                <option>Meghalaya</option>
                <option>Mizoram</option>
                <option>Nagaland</option>
                <option>Odisha</option>
                <option>Punjab</option>
                <option>Rajasthan</option>
                <option>Sikkim</option>
                <option>Tamil Nadu</option>
                <option>Telangana</option>
                <option>Tripura</option>
                <option>Uttar Pradesh</option>
                <option>Uttarakhand</option>
                <option>West Bengal</option>
                <option>Andaman and Nicobar Islands</option>
                <option>Chandigarh</option>
                <option>Dadra and Nagar Haveli and Daman and Diu</option>
                <option>Delhi</option>
                <option>Jammu and Kashmir</option>
                <option>Ladakh</option>
                <option>Lakshadweep</option>
                <option>Puducherry</option>
              </select>
              {errors.state && <span className="vp-error">{errors.state}</span>}
            </div>

            <div className="vp-field-group">
              <label className="vp-label">GST Number <span style={{ color: '#9ca3af', fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
              <input
                type="text"
                name="gst"
                className={`vp-input${errors.gst ? " vp-input-error" : ""}`}
                value={formData.gst}
                onChange={handleGstChange}
                placeholder="e.g. 22ABCDE1234F1Z5"
                maxLength={15}
              />
              {errors.gst && (
                <span className={`vp-error${formData.gst.length === 15 ? "" : " vp-hint"}`}>{errors.gst}</span>
              )}
              {!errors.gst && formData.gst.length === 15 && (
                <span className="vp-success">Valid GST number</span>
              )}
            </div>
          </div>

          {/* Studio Logo Upload */}
          <div className="vp-field-group">
            <label className="vp-label">Studio Logo</label>
            <input type="file" ref={logoInputRef} style={{ display: "none" }} accept="image/*" onChange={handleLogoFileChange} disabled={logoUploading} />
            {formData.studio_logo ? (
              <div className="vp-logo-preview">
                <img src={formData.studio_logo} alt="Studio logo" className="vp-logo-thumb" onError={(e) => { e.target.onerror = null; setFormData((prev) => ({ ...prev, studio_logo: "" })); setPreview(""); }} />
                <div className="vp-logo-actions">
                  <button type="button" className="vp-btn-secondary" onClick={() => logoInputRef.current.click()} disabled={logoUploading}>
                    {logoUploading ? "Uploading…" : "Replace"}
                  </button>
                  <button type="button" className="vp-btn-secondary" onClick={() => { setFormData((prev) => ({ ...prev, studio_logo: "" })); setPreview(""); }}>
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className={`vp-logo-upload-area${logoUploading ? " vp-logo-upload-area--loading" : ""}`} onClick={() => !logoUploading && logoInputRef.current.click()}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                <span>{logoUploading ? "Uploading…" : "Click to upload studio logo"}</span>
                <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>PNG, JPG or GIF</span>
              </div>
            )}
          </div>

          <div className="vp-actions">
            <button type="button" className="vp-btn-secondary" onClick={() => history.goBack()}>
              Cancel
            </button>
            <button type="submit" className="vp-btn-primary" disabled={isSubmitting || logoUploading}>
              {isSubmitting ? "Saving…" : logoUploading ? "Uploading Logo…" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
